/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  LeadBoardListItemDto, LeadsListFilterDto,
  Priority,
  Stage,
  UserDetailsDto,
  UserListItemDto,
} from '@gh/builder-shared-data';
import { DictionaryItem, EnumDictionary, Table } from '@gh/common-shared';
import { ID, WithDirtyType } from '@gh/common-shared-data';

import { filter, sortBy } from 'lodash';

import { buildLeadBoardTable, initLeadBoardInfo } from '../components/lead-board/lead-board.state';
import { VirtualPosition } from '../components/lead-board/virtual-rendering';

export type LeadsListFilter = LeadsListFilterDto;

export type LeadBoardListItem = WithDirtyType<LeadBoardListItemDto & {
  isDragged?: boolean;
}>;
export type LeadBoardMobileFilter = { priority: number; stage: number };

export enum LeadBoardScrollTarget { None, Standard, Mobile }

export type LeadBoardState = {
  info: LeadBoardInfo;
  salespersonList: SalespersonSelectItem[];
  boardTable: Table<LeadBoardListItem>;
  mobileFilter: LeadBoardMobileFilter;
  priority?: Priority;
  stage?: Stage;
  openedId?: ID;
  scroll: {
    target: LeadBoardScrollTarget;
    position: VirtualPosition;
  };
  activeDropZone?: LeadBoardColumnId;
  draggingId?: ID;
};

export type LeadBoardScroll = {
  target: LeadBoardScrollTarget;
  position: VirtualPosition;
};

export type LeadBoardInfo = {
  salesPersonId?: ID;
  searchString?: string;
  showCompleted: boolean;
  showActive: boolean;
  showInactive: boolean;
  showOverdue: boolean;
  showSuppressed: boolean;
};

export type UserDetails = UserDetailsDto;

export type UserListItem = UserListItemDto;
export type SalespersonSelectItem = {
  id: ID;
  name: string;
  status?: string;
};
export const salespersonSelectItemMapper =
  ({ userId, userName, status }: UserListItem) => ({ id: userId, name: userName, status });
export const ALL_SALESPERSON_SELECT_VALUE = '_All';

export const isSalespersonSelectItemInactive = ({ status }: SalespersonSelectItem): boolean =>
  status === 'INACTIVE' || status === 'DELETED';

export type LeadBoardCardMoveDialogConfig = {
  card: LeadBoardListItem;
};

export type LeadBoardColumnId = {
  stage: number;
  priority: number;
};

export const LEAD_BOARD_SCROLL_START = { left: 0, top: 0 };
export const LEAD_BOARD_SCROLL_INIT: LeadBoardScroll = {
  target: LeadBoardScrollTarget.None,
  position: LEAD_BOARD_SCROLL_START,
};
export const LEAD_BOARD_MOBILE_FILTER_INIT: LeadBoardMobileFilter = { priority: 1, stage: 1 };

export const INIT_LEAD_BOARD_STATE_VALUE: LeadBoardState = {
  info: {
    salesPersonId: void 0,
    searchString: void 0,
    showActive: false,
    showCompleted: false,
    showInactive: false,
    showOverdue: false,
    showSuppressed: false,
  },
  salespersonList: [],
  boardTable: buildLeadBoardTable([]),
  mobileFilter: LEAD_BOARD_MOBILE_FILTER_INIT,
  priority: void 0,
  stage: void 0,
  scroll: LEAD_BOARD_SCROLL_INIT,
};

export const cardUpdateParams = ({ stage, priority, followUpDate, contactId, version }: LeadBoardListItem) => ({
  stage,
  priority,
  followUpDate,
  contactId,
  version,
});

export const isLeadBoardItemHasSameColumnId = ({ priority, stage }: LeadBoardListItem, columnId: LeadBoardColumnId) =>
  priority === columnId.priority && stage === columnId.stage;

export function generateBoardColumnCode(priority: DictionaryItem<Priority>, stage: DictionaryItem<Stage>): string {
  return `${priority.id}:${stage.id}$`;
}

/**
 * Composes Lead board column map iteratee according to Card priority and stage
 * @param priority DictionaryItem<Priority>
 * @param stage DictionaryItem<Stage>
 * @return IdentityFn<LeadBoardListItem[]>
 */
export function filterBoardColumnCards(priority: DictionaryItem<any>,
                                       stage: DictionaryItem<any>): IdentityFn<LeadBoardListItem[]> {
  return (list: LeadBoardListItem[]) =>
    filter(list, (item: LeadBoardListItem) =>
      item.priority === priority.id && item.stage === stage.id);
}

/**
 * Sorts Lead board column cards
 * @param list LeadBoardListItem[]
 */
export function sortBoardColumnCards(list: LeadBoardListItem[]): LeadBoardListItem[] {
  return sortBy(list, ['followUpDate', 'contactAddressee']);
}

/**
 * Init Lead board PlainImmutablePageState value
 * @param salesPersonId Maybe<ID>
 * @param salespersonList SalespersonSelectItem[]
 * @param list LeadBoardListItem[]
 * @param priorities EnumDictionary<Priority>
 * @param stages EnumDictionary<Stage>
 * @param leadBordFilter any
 */
export function initLeadBoardPageState(salesPersonId: Maybe<ID>,
                                       salespersonList: SalespersonSelectItem[],
                                       list: LeadBoardListItem[],
                                       priorities: EnumDictionary<Priority>,
                                       stages: EnumDictionary<Stage>,
                                       leadBordFilter: any): LeadBoardState {
  return {
    info: initLeadBoardInfo(salesPersonId, leadBordFilter),
    salespersonList,
    boardTable: buildLeadBoardTable(list),
    priority: void 0,
    stage: void 0,
    mobileFilter: LEAD_BOARD_MOBILE_FILTER_INIT,
    scroll: LEAD_BOARD_SCROLL_INIT,
  };
}
